import { Paper, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowDownSLineIcon from 'remixicon-react/ArrowDownSLineIcon';
import { InsightFilterType } from '../../apis/PythonInsightsAPI';
import { TreeView } from '@mui/x-tree-view';
import ArrowRightSLineIcon from 'remixicon-react/ArrowRightSLineIcon';
import InsightTreeItem from './InsightTreeItem';
import { SyntheticEvent } from 'react';
import { MappedInsightType } from '../../queries/useInsightsQuery';
import {
  GetInsightsSummaryQueryFnType,
  GetInsightsSummaryQueryKeyType,
} from '../../queries/useInsightsSummaryQuery';
import { useLocation } from 'react-router-dom';
import useInsightsStore from 'store/useInsightsStore';

type RenderInsightsProps = {
  includeBWInsights: boolean;
  insightTypeState: Partial<
    Record<
      string,
      {
        expandedInsightIds: string[];
        expandedInsightSummaryIds: string[];
      }
    >
  >;
  filters: InsightFilterType[] | undefined;
  groupBy: string;
  insight: MappedInsightType;
  searchKeyword: string;
  getInsightsSummaryQueryFn?: GetInsightsSummaryQueryFnType;
  getInsightsSummaryQueryKey?: GetInsightsSummaryQueryKeyType;
};

function RenderInsight({
  includeBWInsights,
  insightTypeState,
  filters,
  groupBy,
  insight,
  searchKeyword,
  getInsightsSummaryQueryFn,
  getInsightsSummaryQueryKey,
}: RenderInsightsProps) {
  const { pathname } = useLocation();
  const { setExpandedInsightIds } = useInsightsStore();

  const extractNodeIds = (insight: MappedInsightType) => {
    const ids = [insight.title];
    if (insight.children && insight.children?.length > 0) {
      insight.children.forEach((_insight) => {
        ids.push(...extractNodeIds(_insight));
      });
    }
    return ids;
  };

  const handleNodesToggle = (event: SyntheticEvent, ids: string[], insight: MappedInsightType) => {
    setExpandedInsightIds({
      type: pathname.includes('project') ? 'projectInsightsState' : 'insightsState',
      insightTitle: insight.title,
      groupBy,
      expandedInsightIds: ids.length === 1 ? extractNodeIds(insight) : ids,
    });
  };

  return (
    <Container key={insight.title}>
      <TreeView
        aria-label="insights-tree-view"
        defaultCollapseIcon={
          <IconButton size="small">
            <ArrowDownSLineIcon />
          </IconButton>
        }
        defaultExpandIcon={
          <IconButton size="small">
            <ArrowRightSLineIcon />
          </IconButton>
        }
        expanded={insightTypeState?.[insight.title]?.expandedInsightIds || []}
        onNodeToggle={(...params) => handleNodesToggle(...params, insight)}
      >
        <InsightTreeItem
          includeBWInsights={includeBWInsights}
          parentInsightTitle={insight.title}
          insight={insight}
          expandedInsightSummaryIds={
            insightTypeState?.[insight.title]?.expandedInsightSummaryIds || []
          }
          groupBy={groupBy}
          directory={[insight.title]}
          level={0}
          searchKeyword={searchKeyword}
          filters={filters}
          getInsightsSummaryQueryFn={getInsightsSummaryQueryFn}
          getInsightsSummaryQueryKey={getInsightsSummaryQueryKey}
        />
      </TreeView>
    </Container>
  );
}

const Container = styled(Paper)(({ theme }) => ({
  boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
  padding: '4px 8px',
  borderRadius: 8,
  marginBottom: 8,
}));

export default RenderInsight;
